import React from 'react';
import { MegaphoneIcon } from '@heroicons/react/24/outline';

const FIELD_GROUPS = [
  {
    title: 'UTM parameters',
    fields: [
      { key: 'utm_source', label: 'Source' },
      { key: 'utm_medium', label: 'Medium' },
      { key: 'utm_campaign', label: 'Campaign' },
      { key: 'utm_term', label: 'Term' },
      { key: 'utm_content', label: 'Content' },
      { key: 'utm_id', label: 'ID' },
    ],
  },
  {
    title: 'Ad click IDs',
    fields: [
      { key: 'gclid', label: 'Google (gclid)' },
      { key: 'fbclid', label: 'Facebook (fbclid)' },
      { key: 'msclkid', label: 'Microsoft (msclkid)' },
    ],
  },
  {
    title: 'Landing context',
    fields: [
      { key: 'referrer', label: 'Referrer' },
      { key: 'landing_page', label: 'Landing page' },
      { key: 'captured_at', label: 'Captured at' },
    ],
  },
];

function resolveUtmAttribution({ utm, session, website } = {}) {
  if (utm && typeof utm === 'object') return utm;
  if (session?.context?.utm && typeof session.context.utm === 'object') return session.context.utm;
  if (website?.utm && typeof website.utm === 'object') return website.utm;
  if (website?.requirements?.utm && typeof website.requirements.utm === 'object') {
    return website.requirements.utm;
  }
  return null;
}

function formatCapturedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function FieldRow({ label, value }) {
  if (!value) return null;
  const isLong = value.length > 80 || value.includes('http');
  return (
    <div className={isLong ? 'md:col-span-2' : undefined}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className={`text-sm text-gray-900 ${isLong ? 'font-mono break-all' : ''}`}>{value}</div>
    </div>
  );
}

export default function UtmAttributionCard({ utm, session, website, className = '' }) {
  const data = resolveUtmAttribution({ utm, session, website });
  const hasData = data && Object.values(data).some((v) => String(v || '').trim());

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`.trim()}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-violet-50 rounded-lg">
          <MegaphoneIcon className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">Marketing attribution (UTM)</h2>
          <p className="text-xs text-gray-500 mt-0.5">First-touch campaign params captured at wizard start</p>
        </div>
      </div>

      {!hasData ? (
        <p className="text-sm text-gray-400 italic">No UTM or ad-click data captured for this record.</p>
      ) : (
        <div className="space-y-6">
          {FIELD_GROUPS.map((group) => {
            const visibleFields = group.fields.filter(({ key }) => {
              const raw = data[key];
              return raw != null && String(raw).trim() !== '';
            });
            if (visibleFields.length === 0) return null;

            return (
              <div key={group.title}>
                <div className="text-xs font-semibold text-gray-700 mb-3">{group.title}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleFields.map(({ key, label }) => (
                    <FieldRow
                      key={key}
                      label={label}
                      value={key === 'captured_at' ? formatCapturedAt(data[key]) : String(data[key])}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { resolveUtmAttribution };
